// Permission system for role-based access control
export enum UserRole {
  PLATFORM_ADMIN = 'platformadmin',
  GENERAL_ADMIN = 'general_admin', // New role for top-level agencies
  MAIN_AGENCY_ADMIN = 'mainagencyadmin', // New role from backend
  MSP_ADMIN = 'mspadmin', // MSP agency admins
  AGENCY_ADMIN = 'agencyadmin',
  COMPANY_ADMIN = 'companyadmin',
  CANDIDATE = 'candidate',
  RECRUITER = 'recruiter',
  BUSINESS_ANALYST = 'business_analyst',
}

// Backend role permission types (from /Role/{roleName} endpoint)
// These will be used in the future for fine-grained permission checking
export enum BackendEntityType {
  USER = 'User',
  AGENCY = 'Agency',
  ROLE = 'Role',
  POSITION = 'Position',
  SERVICE_ORDER = 'ServiceOrder',
  SERVICE_ORDER_TEMPLATE = 'ServiceOrderTemplate',
  CANDIDATE = 'Candidate',
  COMPANY = 'Company',
  JOBORDER = 'Joborder',
  APPLICANT = 'Applicant',
  SITE = 'Site',
  WC_CODE = 'WCCode',
  WC_CATALOG = 'WCCatalog',
  PAYCODE = 'Paycode',
  BILLCODE = 'Billcode',
  ASSIGNMENT = 'Assignment',
}

export enum BackendOperationType {
  CREATE = 'Create',
  UPDATE = 'Update',
  UPDATE_STATUS = 'UpdateStatus',
  READ = 'Read',
  DELETE = 'Delete',
}

export enum BackendScopeType {
  ALL = 'All',
  OWN = 'Own',
  MANAGED = 'Managed',
}

export interface BackendAccessItem {
  entityType: BackendEntityType | string
  scope: BackendScopeType | string
  operations: (BackendOperationType | string)[]
}

export interface BackendRolePermissions {
  _id: string
  name: string
  accessList: BackendAccessItem[]
  /** Module permissions array for granular permission checking */
  modulePermissions?: Array<{
    key: string
    name: string
    canView: boolean
    canCreate: boolean
    canEdit: boolean
    canDelete: boolean
    submodules?: Array<{
      key: string
      name: string
      canView: boolean
      canCreate: boolean
      canEdit: boolean
      canDelete: boolean
    }>
  }>
  isPlatform?: boolean
  /** Role category: Platform, MSP, Agency, Company, or Candidate */
  category?: 'Platform' | 'MSP' | 'Agency' | 'Company' | 'Candidate'
  application: string
  createdDate: string
  modifiedDate: string
  versionKey: number
  id: string
}

// Backend MemberAccessRoleType integration
export enum MemberAccessRoleType {
  PlatformAdmin = 'platformadmin',
  General_Admin = 'general_admin', // For parent agencies without MSP status
  MainAgencyAdmin = 'mainagencyadmin', // For main agencies
  MSPAdmin = 'mspadmin', // For MSP agencies
  AgencyAdmin = 'agencyadmin', // For child agencies
  CompanyAdmin = 'companyadmin',
  Candidate = 'candidate',
}

// Role hierarchy levels - higher number = more permissions
export enum RoleHierarchy {
  PLATFORM_ADMIN = 100, // Highest level - can access everything
  GENERAL_ADMIN = 80, // Parent agency admins - can manage child agencies
  MAIN_AGENCY_ADMIN = 75, // Main agency admins
  MSP_ADMIN = 70, // MSP agency admins
  AGENCY_ADMIN = 50, // Child agency admins - limited to their scope
  COMPANY_ADMIN = 30, // Company level access only
  RECRUITER = 20, // Limited recruitment access
  BUSINESS_ANALYST = 10, // Read-only access
  CANDIDATE = 5, // Self-service access only
}

export enum EntityType {
  AGENCY = 'agency',
  BRANCH = 'branch',
  COMPANY = 'company',
  REGION = 'region',
  SITE = 'site',
  DEPARTMENT = 'department',
}

export enum TabPermission {
  // Agency tabs
  AGENCY_PROFILE = 'agency_profile',
  AGENCY_CATALOGS = 'agency_catalogs',
  AGENCY_USERS_ROLES = 'agency_users_roles',
  AGENCY_ORDERS = 'agency_orders',
  AGENCY_DOCUMENTS = 'agency_documents',
  AGENCY_AFFILIATES = 'agency_affiliates',
  AGENCY_METRICS = 'agency_metrics',

  // Branch/Hierarchy permissions
  AGENCY_HIERARCHY = 'agency_hierarchy',
  CREATE_BRANCH = 'create_branch',
  MANAGE_BRANCHES = 'manage_branches',
  VIEW_HIERARCHY = 'view_hierarchy',

  // EOR (Employer of Record) permissions
  EOR_PROFILE = 'eor_profile',
  EOR_TAX_COMPLIANCE = 'eor_tax_compliance',
  EOR_BANKING = 'eor_banking',
  EOR_PAYROLL_SETUP = 'eor_payroll_setup',
  EOR_MANAGED_SITES = 'eor_managed_sites',
  EOR_INVOICE_SETTINGS = 'eor_invoice_settings',

  // Company tabs
  COMPANY_PROFILE = 'company_profile',
  COMPANY_SITES = 'company_sites',
  COMPANY_BILL_CODES = 'company_bill_codes',
  COMPANY_DEPARTMENTS = 'company_departments',
  COMPANY_POSITIONS = 'company_positions',
  COMPANY_TEMPLATES = 'company_templates',
  COMPANY_USERS_ROLES = 'company_users_roles',
  COMPANY_ORDERS = 'company_orders',
  COMPANY_DOCUMENTS = 'company_documents',
  COMPANY_PURCHASE_ORDERS = 'company_purchase_orders',

  // Region tabs
  REGION_PROFILE = 'region_profile',
  REGION_BILL_CODES = 'region_bill_codes',
  REGION_USERS_ROLES = 'region_users_roles',
  REGION_DOCUMENTS = 'region_documents',

  // Site tabs
  SITE_PROFILE = 'site_profile',
  SITE_BILL_CODES = 'site_bill_codes',
  SITE_DEPARTMENTS = 'site_departments',
  SITE_POSITIONS = 'site_positions',
  SITE_USERS_ROLES = 'site_users_roles',
  SITE_DOCUMENTS = 'site_documents',

  // Global tabs
  GLOBAL_COMPENSATION_CODES = 'global_compensation_codes',
  GLOBAL_WC_CATALOGS = 'global_wc_catalogs',
  GLOBAL_PAYCODES = 'global_paycodes',

  // Management permissions
  MANAGE_AGENCIES = 'manage_agencies',
  MANAGE_COMPANIES = 'manage_companies',
  CREATE_AGENCY = 'create_agency',
  CREATE_COMPANY = 'create_company',
  EDIT_ENTITY = 'edit_entity',
  VIEW_ENTITY = 'view_entity',
}

// Role-based permission matrix
export const ROLE_PERMISSIONS: Record<UserRole, TabPermission[]> = {
  [UserRole.PLATFORM_ADMIN]: [
    // Platform admin has access to everything
    ...Object.values(TabPermission),
  ],

  [UserRole.GENERAL_ADMIN]: [
    // General admin can manage agencies and their hierarchy but not MSP-specific features
    TabPermission.MANAGE_AGENCIES,
    TabPermission.CREATE_AGENCY,
    TabPermission.CREATE_BRANCH,
    TabPermission.MANAGE_BRANCHES,
    TabPermission.AGENCY_HIERARCHY,
    TabPermission.VIEW_HIERARCHY,
    TabPermission.AGENCY_PROFILE,
    TabPermission.AGENCY_CATALOGS,
    TabPermission.AGENCY_USERS_ROLES,
    TabPermission.AGENCY_ORDERS,
    TabPermission.AGENCY_DOCUMENTS,
    TabPermission.AGENCY_AFFILIATES,
    TabPermission.AGENCY_METRICS,
    // Limited EOR access (only if their agency is EOR)
    TabPermission.EOR_PROFILE,
    TabPermission.EOR_TAX_COMPLIANCE,
    TabPermission.EOR_BANKING,
    TabPermission.EOR_PAYROLL_SETUP,
    TabPermission.EOR_MANAGED_SITES,
    // Company management
    TabPermission.MANAGE_COMPANIES,
    TabPermission.CREATE_COMPANY,
    TabPermission.COMPANY_PROFILE,
    TabPermission.COMPANY_SITES,
    TabPermission.COMPANY_BILL_CODES,
    TabPermission.COMPANY_DEPARTMENTS,
    TabPermission.COMPANY_POSITIONS,
    TabPermission.COMPANY_TEMPLATES,
    TabPermission.COMPANY_USERS_ROLES,
    TabPermission.COMPANY_ORDERS,
    TabPermission.COMPANY_DOCUMENTS,
    TabPermission.COMPANY_PURCHASE_ORDERS,
    TabPermission.EDIT_ENTITY,
    TabPermission.VIEW_ENTITY,
  ],

  [UserRole.MAIN_AGENCY_ADMIN]: [
    // Main agency admin can manage their agency and branches
    TabPermission.MANAGE_AGENCIES,
    TabPermission.CREATE_BRANCH,
    TabPermission.MANAGE_BRANCHES,
    TabPermission.AGENCY_HIERARCHY,
    TabPermission.VIEW_HIERARCHY,
    TabPermission.AGENCY_PROFILE,
    TabPermission.AGENCY_CATALOGS,
    TabPermission.AGENCY_USERS_ROLES,
    TabPermission.AGENCY_ORDERS,
    TabPermission.AGENCY_DOCUMENTS,
    TabPermission.AGENCY_AFFILIATES,
    TabPermission.AGENCY_METRICS,
    // EOR access if their agency is EOR
    TabPermission.EOR_PROFILE,
    TabPermission.EOR_TAX_COMPLIANCE,
    TabPermission.EOR_BANKING,
    TabPermission.EOR_PAYROLL_SETUP,
    TabPermission.EOR_MANAGED_SITES,
    // Company management
    TabPermission.MANAGE_COMPANIES,
    TabPermission.CREATE_COMPANY,
    TabPermission.COMPANY_PROFILE,
    TabPermission.COMPANY_SITES,
    TabPermission.COMPANY_BILL_CODES,
    TabPermission.COMPANY_DEPARTMENTS,
    TabPermission.COMPANY_POSITIONS,
    TabPermission.COMPANY_TEMPLATES,
    TabPermission.COMPANY_USERS_ROLES,
    TabPermission.COMPANY_ORDERS,
    TabPermission.COMPANY_DOCUMENTS,
    TabPermission.COMPANY_PURCHASE_ORDERS,
    TabPermission.EDIT_ENTITY,
    TabPermission.VIEW_ENTITY,
  ],

  [UserRole.MSP_ADMIN]: [
    // MSP can manage agencies and companies under their control
    TabPermission.MANAGE_AGENCIES,
    TabPermission.MANAGE_COMPANIES,
    TabPermission.CREATE_AGENCY,
    TabPermission.CREATE_COMPANY,
    TabPermission.CREATE_BRANCH,
    TabPermission.MANAGE_BRANCHES,
    TabPermission.AGENCY_HIERARCHY,
    TabPermission.VIEW_HIERARCHY,
    TabPermission.AGENCY_PROFILE,
    TabPermission.AGENCY_CATALOGS,
    TabPermission.AGENCY_USERS_ROLES,
    TabPermission.AGENCY_ORDERS,
    TabPermission.AGENCY_DOCUMENTS,
    TabPermission.AGENCY_AFFILIATES,
    TabPermission.AGENCY_METRICS,
    TabPermission.EOR_PROFILE,
    TabPermission.EOR_TAX_COMPLIANCE,
    TabPermission.EOR_BANKING,
    TabPermission.EOR_PAYROLL_SETUP,
    TabPermission.EOR_MANAGED_SITES,
    TabPermission.COMPANY_PROFILE,
    TabPermission.COMPANY_SITES,
    TabPermission.COMPANY_BILL_CODES,
    TabPermission.COMPANY_DEPARTMENTS,
    TabPermission.COMPANY_POSITIONS,
    TabPermission.COMPANY_TEMPLATES,
    TabPermission.COMPANY_USERS_ROLES,
    TabPermission.COMPANY_ORDERS,
    TabPermission.COMPANY_DOCUMENTS,
    TabPermission.COMPANY_PURCHASE_ORDERS,
    TabPermission.REGION_PROFILE,
    TabPermission.REGION_BILL_CODES,
    TabPermission.REGION_USERS_ROLES,
    TabPermission.REGION_DOCUMENTS,
    TabPermission.SITE_PROFILE,
    TabPermission.SITE_BILL_CODES,
    TabPermission.SITE_DEPARTMENTS,
    TabPermission.SITE_POSITIONS,
    TabPermission.SITE_USERS_ROLES,
    TabPermission.SITE_DOCUMENTS,
    TabPermission.EDIT_ENTITY,
    TabPermission.VIEW_ENTITY,
  ],

  [UserRole.AGENCY_ADMIN]: [
    // Agency admin can only manage their own agency and assigned companies
    TabPermission.VIEW_HIERARCHY, // Can view but not modify hierarchy
    TabPermission.AGENCY_PROFILE,
    TabPermission.AGENCY_SITES,
    TabPermission.AGENCY_COMPANIES,
    TabPermission.AGENCY_CATALOGS,
    TabPermission.AGENCY_USERS_ROLES,
    TabPermission.AGENCY_ORDERS,
    TabPermission.AGENCY_DOCUMENTS,
    TabPermission.AGENCY_METRICS,
    // EOR permissions for agencies with EOR enabled
    TabPermission.EOR_PROFILE,
    TabPermission.EOR_TAX_COMPLIANCE,
    TabPermission.EOR_BANKING,
    TabPermission.EOR_PAYROLL_SETUP,
    TabPermission.EOR_MANAGED_SITES,
    TabPermission.COMPANY_PROFILE,
    TabPermission.COMPANY_SITES,
    TabPermission.COMPANY_BILL_CODES,
    TabPermission.COMPANY_DEPARTMENTS,
    TabPermission.COMPANY_POSITIONS,
    TabPermission.COMPANY_TEMPLATES,
    TabPermission.COMPANY_USERS_ROLES,
    TabPermission.COMPANY_ORDERS,
    TabPermission.COMPANY_DOCUMENTS,
    TabPermission.COMPANY_PURCHASE_ORDERS,
    // Region and Site permissions - needed to view managed sites
    TabPermission.REGION_PROFILE,
    TabPermission.REGION_BILL_CODES,
    TabPermission.REGION_USERS_ROLES,
    TabPermission.REGION_DOCUMENTS,
    TabPermission.SITE_PROFILE,
    TabPermission.SITE_BILL_CODES,
    TabPermission.SITE_DEPARTMENTS,
    TabPermission.SITE_POSITIONS,
    TabPermission.SITE_USERS_ROLES,
    TabPermission.SITE_DOCUMENTS,
    TabPermission.VIEW_ENTITY,
  ],

  [UserRole.COMPANY_ADMIN]: [
    // Company admin can only manage their own company
    TabPermission.COMPANY_PROFILE,
    TabPermission.COMPANY_SITES,
    TabPermission.COMPANY_BILL_CODES,
    TabPermission.COMPANY_DEPARTMENTS,
    TabPermission.COMPANY_POSITIONS,
    TabPermission.COMPANY_TEMPLATES,
    TabPermission.COMPANY_USERS_ROLES,
    TabPermission.COMPANY_ORDERS,
    TabPermission.COMPANY_DOCUMENTS,
    TabPermission.COMPANY_PURCHASE_ORDERS,
    TabPermission.REGION_PROFILE,
    TabPermission.REGION_BILL_CODES,
    TabPermission.REGION_USERS_ROLES,
    TabPermission.REGION_DOCUMENTS,
    TabPermission.SITE_PROFILE,
    TabPermission.SITE_BILL_CODES,
    TabPermission.SITE_DEPARTMENTS,
    TabPermission.SITE_POSITIONS,
    TabPermission.SITE_USERS_ROLES,
    TabPermission.SITE_DOCUMENTS,
    TabPermission.VIEW_ENTITY,
  ],

  [UserRole.RECRUITER]: [
    // Recruiter has limited view access
    TabPermission.COMPANY_POSITIONS,
    TabPermission.COMPANY_ORDERS,
    TabPermission.VIEW_ENTITY,
  ],

  [UserRole.BUSINESS_ANALYST]: [
    // Business analyst has read-only access to most data
    TabPermission.AGENCY_PROFILE,
    TabPermission.AGENCY_ORDERS,
    TabPermission.COMPANY_PROFILE,
    TabPermission.COMPANY_POSITIONS,
    TabPermission.COMPANY_ORDERS,
    TabPermission.VIEW_ENTITY,
  ],

  [UserRole.CANDIDATE]: [TabPermission.VIEW_ENTITY],
}

// Tab configuration for different entity types
export interface TabConfig {
  label: string
  value: number | string
  permission: TabPermission
  disabled?: boolean
  route?: string
}

export const AGENCY_TABS: TabConfig[] = [
  {label: 'Profile', value: 0, permission: TabPermission.AGENCY_PROFILE, route: 'profile'},
  {label: 'Catalogs', value: 1, permission: TabPermission.AGENCY_CATALOGS, route: 'catalogs'},
  {
    // STFFLG-4014: renamed from "Users & Roles". The route and permission key are unchanged —
    // existing links keep working, and the seeder strips any permission key that drifts from
    // config, so renaming `agency_users_roles` would delete every customised flag on it.
    label: 'Internal Employees',
    value: 2,
    permission: TabPermission.AGENCY_USERS_ROLES,
    route: 'users-roles',
  },
  {label: 'Orders', value: 3, permission: TabPermission.AGENCY_ORDERS, route: 'orders'},
  {label: 'Documents', value: 4, permission: TabPermission.AGENCY_DOCUMENTS, route: 'documents'},
  {label: 'Affiliates', value: 5, permission: TabPermission.AGENCY_AFFILIATES, route: 'affiliates'},
  {label: 'Metrics', value: 6, permission: TabPermission.AGENCY_METRICS, route: 'metrics'},
]

export const COMPANY_TABS: TabConfig[] = [
  {label: 'Profile', value: 0, permission: TabPermission.COMPANY_PROFILE, route: 'profile'},
  {label: 'Sites', value: 1, permission: TabPermission.COMPANY_SITES, route: 'sites'},
  {
    label: 'Bill Codes',
    value: 2,
    permission: TabPermission.COMPANY_BILL_CODES,
    route: 'bill-codes',
  },
  {
    label: 'Departments',
    value: 3,
    permission: TabPermission.COMPANY_DEPARTMENTS,
    route: 'departments',
  },
  {
    label: 'Users',
    value: 4,
    permission: TabPermission.COMPANY_USERS_ROLES,
    route: 'users-roles',
  },
  {label: 'Positions', value: 5, permission: TabPermission.COMPANY_POSITIONS, route: 'positions'},
  {label: 'Templates', value: 6, permission: TabPermission.COMPANY_TEMPLATES, route: 'templates'},
  {label: 'Orders', value: 7, permission: TabPermission.COMPANY_ORDERS, route: 'orders'},
  {label: 'Documents', value: 8, permission: TabPermission.COMPANY_DOCUMENTS, route: 'documents'},
  {
    label: 'PO(s)',
    value: 9,
    permission: TabPermission.COMPANY_PURCHASE_ORDERS,
    route: 'purchase-orders',
  },
]

export const REGION_TABS: TabConfig[] = [
  {label: 'Profile', value: 0, permission: TabPermission.REGION_PROFILE, route: 'profile'},
  {label: 'Bill Codes', value: 1, permission: TabPermission.REGION_BILL_CODES, route: 'bill-codes'},
  {
    label: 'Users & Roles',
    value: 2,
    permission: TabPermission.REGION_USERS_ROLES,
    route: 'users-roles',
  },
  {label: 'Documents', value: 3, permission: TabPermission.REGION_DOCUMENTS, route: 'documents'},
]

export const SITE_TABS: TabConfig[] = [
  {label: 'Profile', value: 0, permission: TabPermission.SITE_PROFILE, route: 'profile'},
  {label: 'Bill Codes', value: 1, permission: TabPermission.SITE_BILL_CODES, route: 'bill-codes'},
  {
    label: 'Departments',
    value: 2,
    permission: TabPermission.SITE_DEPARTMENTS,
    route: 'departments',
  },
  {label: 'Positions', value: 3, permission: TabPermission.SITE_POSITIONS, route: 'positions'},
  {
    label: 'Users & Roles',
    value: 4,
    permission: TabPermission.SITE_USERS_ROLES,
    route: 'users-roles',
  },
  {label: 'Documents', value: 5, permission: TabPermission.SITE_DOCUMENTS, route: 'documents'},
]

// Utility functions
export const hasPermission = (userRole: UserRole, permission: TabPermission): boolean => {
  return ROLE_PERMISSIONS[userRole]?.includes(permission) || false
}

export const getAvailableTabs = (userRole: UserRole, tabConfigs: TabConfig[]): TabConfig[] => {
  return tabConfigs.filter((tab) => hasPermission(userRole, tab.permission))
}

export const canAccessRoute = (userRole: UserRole, requiredPermission: TabPermission): boolean => {
  return hasPermission(userRole, requiredPermission)
}

// Enhanced hierarchical permission functions
export const getRoleHierarchyLevel = (userRole: UserRole): number => {
  switch (userRole) {
    case UserRole.PLATFORM_ADMIN:
      return RoleHierarchy.PLATFORM_ADMIN
    case UserRole.GENERAL_ADMIN:
      return RoleHierarchy.GENERAL_ADMIN
    case UserRole.MAIN_AGENCY_ADMIN:
      return RoleHierarchy.MAIN_AGENCY_ADMIN
    case UserRole.MSP_ADMIN:
      return RoleHierarchy.MSP_ADMIN
    case UserRole.AGENCY_ADMIN:
      return RoleHierarchy.AGENCY_ADMIN
    case UserRole.COMPANY_ADMIN:
      return RoleHierarchy.COMPANY_ADMIN
    case UserRole.RECRUITER:
      return RoleHierarchy.RECRUITER
    case UserRole.BUSINESS_ANALYST:
      return RoleHierarchy.BUSINESS_ANALYST
    case UserRole.CANDIDATE:
      return RoleHierarchy.CANDIDATE
    default:
      return 0
  }
}

export const canManageEntity = (
  userRole: UserRole,
  entityType: EntityType,
  targetRole?: UserRole,
): boolean => {
  const userLevel = getRoleHierarchyLevel(userRole)
  const targetLevel = targetRole ? getRoleHierarchyLevel(targetRole) : 0

  // Users can only manage entities/users at their level or below
  if (targetRole && userLevel <= targetLevel) {
    return false
  }

  switch (entityType) {
    case EntityType.AGENCY:
    case EntityType.BRANCH:
      return userLevel >= RoleHierarchy.GENERAL_ADMIN
    case EntityType.COMPANY:
      return userLevel >= RoleHierarchy.MSP_ADMIN
    case EntityType.REGION:
    case EntityType.SITE:
      return userLevel >= RoleHierarchy.AGENCY_ADMIN
    case EntityType.DEPARTMENT:
      return userLevel >= RoleHierarchy.COMPANY_ADMIN
    default:
      return false
  }
}

export const canCreateBranch = (userRole: UserRole): boolean => {
  return hasPermission(userRole, TabPermission.CREATE_BRANCH)
}

export const canViewHierarchy = (userRole: UserRole): boolean => {
  return hasPermission(userRole, TabPermission.VIEW_HIERARCHY)
}

export const canManageHierarchy = (userRole: UserRole): boolean => {
  return hasPermission(userRole, TabPermission.AGENCY_HIERARCHY)
}

// Map frontend UserRole to backend MemberAccessRoleType
export const mapToMemberAccessRole = (
  userRole: UserRole,
  isEor?: boolean,
  isMsp?: boolean,
): MemberAccessRoleType => {
  switch (userRole) {
    case UserRole.PLATFORM_ADMIN:
      return MemberAccessRoleType.PlatformAdmin
    case UserRole.GENERAL_ADMIN:
      return MemberAccessRoleType.General_Admin
    case UserRole.MAIN_AGENCY_ADMIN:
      return MemberAccessRoleType.MainAgencyAdmin
    case UserRole.MSP_ADMIN:
      return MemberAccessRoleType.MSPAdmin
    case UserRole.AGENCY_ADMIN:
      return MemberAccessRoleType.AgencyAdmin
    case UserRole.COMPANY_ADMIN:
      return MemberAccessRoleType.CompanyAdmin
    case UserRole.CANDIDATE:
      return MemberAccessRoleType.Candidate
    default:
      return MemberAccessRoleType.AgencyAdmin // Default fallback
  }
}

// Map backend MemberAccessRoleType to frontend UserRole
export const mapFromMemberAccessRole = (memberRole: MemberAccessRoleType): UserRole => {
  switch (memberRole) {
    case MemberAccessRoleType.PlatformAdmin:
      return UserRole.PLATFORM_ADMIN
    case MemberAccessRoleType.General_Admin:
      return UserRole.GENERAL_ADMIN
    case MemberAccessRoleType.MainAgencyAdmin:
      return UserRole.MAIN_AGENCY_ADMIN
    case MemberAccessRoleType.MSPAdmin:
      return UserRole.MSP_ADMIN
    case MemberAccessRoleType.AgencyAdmin:
      return UserRole.AGENCY_ADMIN
    case MemberAccessRoleType.CompanyAdmin:
      return UserRole.COMPANY_ADMIN
    case MemberAccessRoleType.Candidate:
      return UserRole.CANDIDATE
    default:
      return UserRole.AGENCY_ADMIN // Default fallback
  }
}

// Route permission mapping
export const ROUTE_PERMISSIONS: Record<string, TabPermission> = {
  // Agency routes
  '/manage-agencies': TabPermission.MANAGE_AGENCIES,
  '/manage-agencies/new': TabPermission.CREATE_AGENCY,
  '/manage-agencies/:id/profile': TabPermission.AGENCY_PROFILE,
  '/manage-agencies/:id/catalogs': TabPermission.AGENCY_CATALOGS,
  '/manage-agencies/:id/users-roles': TabPermission.AGENCY_USERS_ROLES,
  '/manage-agencies/:id/orders': TabPermission.AGENCY_ORDERS,
  '/manage-agencies/:id/documents': TabPermission.AGENCY_DOCUMENTS,
  '/manage-agencies/:id/affiliates': TabPermission.AGENCY_AFFILIATES,

  // Branch/Hierarchy routes
  '/manage-agencies/:id/branches/create': TabPermission.CREATE_BRANCH,
  '/manage-agencies/:id/hierarchy': TabPermission.AGENCY_HIERARCHY,

  // Company routes
  '/manage-companies': TabPermission.MANAGE_COMPANIES,
  '/manage-companies/create/profile': TabPermission.CREATE_COMPANY,
  '/manage-companies/:id/profile': TabPermission.COMPANY_PROFILE,
  '/manage-companies/:id/bill-codes': TabPermission.COMPANY_BILL_CODES,
  '/manage-companies/:id/positions': TabPermission.COMPANY_POSITIONS,
  '/manage-companies/:id/users-roles': TabPermission.COMPANY_USERS_ROLES,
  '/manage-companies/:id/orders': TabPermission.COMPANY_ORDERS,
  '/manage-companies/:id/documents': TabPermission.COMPANY_DOCUMENTS,
  '/manage-companies/:id/purchase-orders': TabPermission.COMPANY_PURCHASE_ORDERS,
  '/manage-companies/:id/departments': TabPermission.COMPANY_DEPARTMENTS,

  // Global routes
  '/global/compensation-codes': TabPermission.GLOBAL_COMPENSATION_CODES,
  '/global/wc-catalogs': TabPermission.GLOBAL_WC_CATALOGS,
  '/global/paycodes': TabPermission.GLOBAL_PAYCODES,
}

// ============================================================================
// FUTURE: Backend Fine-Grained Permission Checking
// ============================================================================
// These functions will be used when the backend role endpoint is enhanced
// Currently unused but prepared for custom role/permission migration

/**
 * Check if a backend role has permission to perform an operation on an entity type
 * @param rolePermissions - Backend role permissions from /Role/{roleName} endpoint
 * @param entityType - Entity type to check (e.g., 'Company', 'User')
 * @param operation - Operation to check (e.g., 'Create', 'Read')
 * @param scope - Optional scope check (e.g., 'All', 'Own')
 * @returns true if the role has the required permission
 *
 * @example
 * const roleData = await fetchRolePermissions('platformadmin')
 * const canCreateCompany = hasBackendPermission(roleData, BackendEntityType.COMPANY, BackendOperationType.CREATE)
 */
export const hasBackendPermission = (
  rolePermissions: BackendRolePermissions | null,
  entityType: BackendEntityType | string,
  operation: BackendOperationType | string,
  scope?: BackendScopeType | string,
): boolean => {
  if (!rolePermissions || !rolePermissions.accessList) {
    return false
  }

  const accessItem = rolePermissions.accessList.find((item) => item.entityType === entityType)

  if (!accessItem) {
    return false
  }

  // Check if operation is allowed
  const hasOperation = accessItem.operations.includes(operation)

  // If scope is specified, check if it matches
  if (scope && accessItem.scope !== scope) {
    return false
  }

  return hasOperation
}

/**
 * Check if a backend role can perform multiple operations on an entity
 * @param rolePermissions - Backend role permissions
 * @param entityType - Entity type to check
 * @param operations - Array of operations to check
 * @returns true if ALL operations are allowed
 */
export const hasAllBackendPermissions = (
  rolePermissions: BackendRolePermissions | null,
  entityType: BackendEntityType | string,
  operations: (BackendOperationType | string)[],
): boolean => {
  return operations.every((op) => hasBackendPermission(rolePermissions, entityType, op))
}

/**
 * Check if a backend role can perform ANY of the specified operations on an entity
 * @param rolePermissions - Backend role permissions
 * @param entityType - Entity type to check
 * @param operations - Array of operations to check
 * @returns true if ANY operation is allowed
 */
export const hasAnyBackendPermission = (
  rolePermissions: BackendRolePermissions | null,
  entityType: BackendEntityType | string,
  operations: (BackendOperationType | string)[],
): boolean => {
  return operations.some((op) => hasBackendPermission(rolePermissions, entityType, op))
}

/**
 * Get all entity types a role has access to
 * @param rolePermissions - Backend role permissions
 * @returns Array of entity types the role can access
 */
export const getAccessibleEntities = (
  rolePermissions: BackendRolePermissions | null,
): (BackendEntityType | string)[] => {
  if (!rolePermissions || !rolePermissions.accessList) {
    return []
  }

  return rolePermissions.accessList.map((item) => item.entityType)
}

/**
 * Check if a role is a platform admin based on backend data
 * @param rolePermissions - Backend role permissions
 * @returns true if the role has platform admin privileges
 */
export const isBackendPlatformAdmin = (rolePermissions: BackendRolePermissions | null): boolean => {
  return rolePermissions?.isPlatform === true
}
